import classNames from "classnames";
import { useState, type ReactNode } from "react";
import { match } from "ts-pattern";
import { getInputSchema, getOutputSchema } from "../../schema";
import type {
  AnyAnyOfCogPropertySchema,
  AnyArrayCogPropertySchema,
  BooleanCogPropertySchema,
  NumericCogPropertySchema,
  ObjectCogPropertySchema,
  StringCogPropertySchema,
  UnknownCogPropertySchema,
  Version,
} from "../../types";
import CodeBlock from "../code-block";

export function InputParameters({
  version,
}: {
  version: Version;
}): ReactNode {
  const [mode, setMode] = useState<"table" | "json">("table");
  const inputSchema = getInputSchema(version._extras);

  return (
    <div className="not-prose">
      <div className="group flex items-center gap-1 mb-4">
        <button
          className="rounded-full text-r8-sm data-[active='true']:bg-r8-gray-12 data-[active='true']:text-r8-gray-1 text-r8-gray-12 hover:bg-r8-gray-6 py-1 px-4 flex-shrink-0"
          type="button"
          data-active={mode === "table"}
          onClick={() => setMode("table")}
        >
          Table
        </button>
        <button
          className="rounded-full text-r8-sm data-[active='true']:bg-r8-gray-12 data-[active='true']:text-r8-gray-1 text-r8-gray-12 hover:bg-r8-gray-6 py-1 px-4 flex-shrink-0"
          type="button"
          data-active={mode === "json"}
          onClick={() => setMode("json")}
        >
          JSON
        </button>
      </div>
      <div>
        {mode === "json" ? (
          <CodeBlock
            textContent={JSON.stringify(inputSchema, null, 2)}
            language="json"
          />
        ) : (
          <div className="divide-y divide-r8-gray-6 *:py-4 *:first-child:pt-0 *:last-child:pb-0 min-w-0 flex-1">
            {Object.entries(inputSchema.properties).map(
              ([name, { title, description, ...rest }]) => {
                return (
                  <div key={name}>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-3">
                        <span className="text-r8-sm font-mono">{name}</span>
                        <span className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
                          {"format" in rest && rest.format
                            ? rest.format
                            : // TODO: Handle anyOf types.
                              "type" in rest && rest.type
                              ? rest.type
                              : "unknown"}
                        </span>
                      </div>
                      <p className="text-r8-sm text-r8-gray-11">
                        {description}
                      </p>
                      {rest.default ||
                      ("minimum" in rest && rest.minimum) ||
                      ("maximum" in rest && rest.maximum) ? (
                        <dl className="font-mono text-r8-xs text-r8-gray-11 inline-flex border divide-x divide-r8-gray-6 !mt-1">
                          {rest.default ? (
                            <div className="flex items-center gap-2 px-2 py-0.5">
                              <dt className="text-r8-gray-10">Default</dt>
                              <dd>{JSON.stringify(rest.default)}</dd>
                            </div>
                          ) : null}
                          {"minimum" in rest && rest.minimum ? (
                            <div className="flex items-center gap-2 px-2 py-0.5">
                              <dt className="text-r8-gray-10">Minimum</dt>
                              <dd>{rest.minimum}</dd>
                            </div>
                          ) : null}
                          {"maximum" in rest && rest.maximum ? (
                            <div className="flex items-center gap-2 px-2 py-0.5">
                              <dt className="text-r8-gray-10">Maximum</dt>
                              <dd>{rest.maximum}</dd>
                            </div>
                          ) : null}
                        </dl>
                      ) : null}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function OutputParameters({
  version,
}: {
  version: Version;
}): ReactNode {
  const outputSchema = getOutputSchema(version._extras);

  const hasRenderableSchema = "type" in outputSchema;
  const [mode, setMode] = useState<"table" | "json">(
    hasRenderableSchema ? "table" : "json"
  );

  return (
    <div className="not-prose">
      <div className="group flex items-center gap-1 mb-4">
        {hasRenderableSchema && (
          <button
            className="rounded-full text-r8-sm data-[active='true']:bg-r8-gray-12 data-[active='true']:text-r8-gray-1 text-r8-gray-12 hover:bg-r8-gray-6 py-1 px-4 flex-shrink-0"
            type="button"
            data-active={mode === "table"}
            onClick={() => setMode("table")}
          >
            Table
          </button>
        )}
        <button
          className="rounded-full text-r8-sm data-[active='true']:bg-r8-gray-12 data-[active='true']:text-r8-gray-1 text-r8-gray-12 hover:bg-r8-gray-6 py-1 px-4 flex-shrink-0"
          type="button"
          data-active={mode === "json"}
          onClick={() => setMode("json")}
        >
          JSON
        </button>
      </div>

      {mode === "json" ? (
        <CodeBlock
          textContent={JSON.stringify(outputSchema, null, 2)}
          language="json"
        />
      ) : hasRenderableSchema ? (
        match(outputSchema)
          .with({ type: "array" }, (schema) => <ArrayOutput schema={schema} />)
          .with({ type: "object" }, (schema) => (
            <ObjectOutput schema={schema} />
          ))
          .otherwise((schema) => <DefaultOutput schema={schema} />)
      ) : null}
    </div>
  );
}

function ArrayOutput({
  schema,
}: {
  schema: AnyArrayCogPropertySchema;
}): ReactNode {
  return (
    <dl className="flex items-center gap-3">
      <dt className="text-r8-sm">Type</dt>
      <dd className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
        {"format" in schema.items && schema.items.format
          ? schema.items.format
          : schema.items.type || "unknown"}
        []
      </dd>
    </dl>
  );
}

function ObjectOutput({
  name,
  schema,
}: {
  name?: string;
  schema: ObjectCogPropertySchema;
}): ReactNode {
  return (
    <div className="space-y-0.5">
      {name && (
        <>
          <div className="flex items-center gap-3">
            <span className="text-r8-sm font-mono">{name}</span>
            <span className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
              {schema.type}
            </span>
          </div>
          {schema.description && (
            <p className="text-r8-sm text-r8-gray-11">{schema.description}</p>
          )}
        </>
      )}
      {schema.properties && (
        <>
          {name && <p className="text-r8-sm text-r8-gray-11">Properties:</p>}
          <div
            className={classNames(
              "divide-y divide-r8-gray-6 *:py-3 *:first-child:pt-0 *:last-child:pb-0 min-w-0 flex-1",
              {
                "pl-5": name,
              }
            )}
          >
            {Object.entries(schema.properties).map(([name, propertySchema]) =>
              match(propertySchema)
                .with({ type: "array" }, (schema) => (
                  <ArrayOutputProperty key={name} name={name} schema={schema} />
                ))
                .with({ type: "object" }, (schema) => (
                  <ObjectOutput key={name} name={name} schema={schema} />
                ))
                .otherwise((schema) => (
                  <DefaultOutputProperty
                    key={name}
                    name={name}
                    schema={schema}
                  />
                ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DefaultOutput({
  schema,
}: {
  schema:
    | StringCogPropertySchema
    | NumericCogPropertySchema
    | BooleanCogPropertySchema
    | UnknownCogPropertySchema;
}): ReactNode {
  return (
    <dl className="flex items-center gap-3">
      <dt className="text-r8-sm">Type</dt>
      <dd className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
        {"format" in schema && schema.format
          ? schema.format
          : schema.type || "unknown"}
      </dd>
    </dl>
  );
}

function ArrayOutputProperty({
  name,
  schema,
}: {
  name: string;
  schema: AnyArrayCogPropertySchema;
}): ReactNode {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-3">
        {name && <span className="text-r8-sm font-mono">{name}</span>}
        <span className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
          {"format" in schema.items && schema.items.format
            ? schema.items.format
            : schema.items.type || "unknown"}
          []
        </span>
      </div>
      {schema.description && (
        <p className="text-r8-sm text-r8-gray-11">{schema.description}</p>
      )}
    </div>
  );
}

function DefaultOutputProperty({
  name,
  schema,
}: {
  name: string;
  schema:
    | StringCogPropertySchema
    | NumericCogPropertySchema
    | BooleanCogPropertySchema
    | UnknownCogPropertySchema
    | AnyAnyOfCogPropertySchema;
}): ReactNode {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-3">
        {name && <span className="text-r8-sm font-mono">{name}</span>}
        <span className="text-r8-xs font-mono bg-r8-gray-2 px-2 my-0.5">
          {"format" in schema && schema.format
            ? schema.format
            : // TODO: Handle anyOf types.
              "type" in schema && schema.type
              ? schema.type
              : "unknown"}
        </span>
      </div>
      {schema.description && (
        <p className="text-r8-sm text-r8-gray-11">{schema.description}</p>
      )}
    </div>
  );
}
